// Steuerberechnung nach § 32a EStG 2026 (Steuerfortentwicklungsgesetz v. 23.12.2024)
// Rein funktionale Berechnung ohne React-Abhängigkeiten

export interface TaxResult {
  abgabenquote: number;
  steuerTageImJahr: number;
  staatProTag: number;
  taxFreedomDay: string;
  netto: number;
  abgabenGesamt: number;
  breakevenBtc: number;
  breakevenEtf: number;
  breakevenAktien: number;
}

// Einkommensteuer nach § 32a EStG 2026
function calcEinkommensteuer(zvE: number): number {
  if (zvE <= 12348) return 0;
  if (zvE <= 17799) {
    const y = (zvE - 12348) / 10000;
    return Math.floor((914.51 * y + 1400) * y);
  }
  if (zvE <= 69878) {
    const z = (zvE - 17799) / 10000;
    return Math.floor((173.1 * z + 2397) * z + 1034.87);
  }
  if (zvE <= 277825) {
    return Math.floor(0.42 * zvE - 11135.63);
  }
  return Math.floor(0.45 * zvE - 19470.38);
}

// Beitragsbemessungsgrenzen 2026
const BBG_KV = 69750;
const BBG_RV = 101400;

// Sparerpauschbetrag (Single)
const SPARERPAUSCHBETRAG = 1000;

export function calcAll(bruttoMonat: number): TaxResult {
  const bruttoJahr = bruttoMonat * 12;

  // Zu versteuerndes Einkommen (Werbungskostenpauschale + Sonderausgabenpauschale)
  const zvE = Math.max(0, bruttoJahr - 1230 - 36);

  // Einkommensteuer
  const est = calcEinkommensteuer(zvE);

  // Solidaritätszuschlag (Freigrenze 20.350 EUR)
  const soli = est > 20350 ? est * 0.055 : 0;

  // Sozialabgaben (AN-Anteil)
  const kvBrutto = Math.min(bruttoJahr, BBG_KV);
  const rvBrutto = Math.min(bruttoJahr, BBG_RV);

  const kv = kvBrutto * ((14.6 + 2.9) / 2 / 100); // KV + Zusatzbeitrag, AN-Hälfte
  const pv = kvBrutto * 0.024; // PV kinderlos (3,6% Basis + 0,6% Zuschlag, AN: 2,4%)
  const rv = rvBrutto * 0.093; // RV AN-Anteil
  const av = rvBrutto * 0.013; // AV AN-Anteil
  const sozial = kv + pv + rv + av;

  const abgabenGesamt = est + soli + sozial;
  const netto = bruttoJahr - abgabenGesamt;
  const abgabenquote =
    bruttoJahr > 0 ? (abgabenGesamt / bruttoJahr) * 100 : 0;

  // Lebenszeit-Kennzahlen
  const steuerTageImJahr = (abgabenquote / 100) * 365;
  const staatProTag = abgabenGesamt / 365;

  // Tax Freedom Day
  const freiAbTag = new Date(2026, 0, 1);
  freiAbTag.setDate(freiAbTag.getDate() + Math.floor(steuerTageImJahr));
  const taxFreedomDay = freiAbTag.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
  });

  // Breakeven-Berechnung
  const breakevenBtc = abgabenGesamt; // Steuerfrei nach >1 Jahr
  const etfEffektivSatz = 0.26375 * 0.7; // 30% Teilfreistellung
  const breakevenEtf =
    abgabenGesamt / (1 - etfEffektivSatz) + SPARERPAUSCHBETRAG;
  const breakevenAktien =
    abgabenGesamt / (1 - 0.26375) + SPARERPAUSCHBETRAG;

  return {
    abgabenquote,
    steuerTageImJahr,
    staatProTag,
    taxFreedomDay,
    netto,
    abgabenGesamt,
    breakevenBtc,
    breakevenEtf,
    breakevenAktien,
  };
}
